import {
    login,
    logout,
    logoutOfSso,
    currentUser,
    getSsoOptions
} from "@/features/account/services/accountService";
import { goTo, currentRouteIsPublic } from "@/services/routeService";
import { clearSsoLinkRequest } from "@/services/ssoLinkRequest";
import { handleError } from "@/services/errorUtility";
import { createCompletion } from "@/services/completion";
import { DateTime } from "luxon";

function getDefaultState() {
    return {
        user: null,
        // How SSO is configured - app-wide, so loaded once and kept when the user logs out.
        ssoOptions: { enabled: false, required: false, passwordChangeAvailable: false },
        ssoOptionsLoaded: false,
        policies: null,
        isLoggedIn: false,
        isLoaded: false,
        loggingIn: false,
        loadCompletion: createCompletion()
    }
}

const state = getDefaultState();

const getters = {
    personId: (state) => {
        return state.user?.personId;
    },
    hasPolicy: state => policy => {
        return state.policies?.has(policy) ?? false;
    },
    isInGracePeriod: (state) => {
        if (!state.user || !state.user.renewsAt) {
            return false;
        }

        const renewalDate = DateTime.fromISO(state.user.renewsAt);
        const now = DateTime.local();

        return renewalDate < now;
    }
}

const actions = {
    async load({ commit, dispatch }) {
        await Promise.all([dispatch("refreshUser"), dispatch("loadSsoOptions")]);
        commit("loaded");
        state.loadCompletion.complete();
    },
    async waitForLoad({ state }) {
        await state.loadCompletion();
    },
    async login({ commit, dispatch }, { emailAddress, password }) {
        try {
            commit("loggingIn", true);
            await login(emailAddress, password);
            await dispatch("enterApplication");
        }
        catch (error) {
            commit("clear", error);
            handleError(error);
        }
        finally {
            commit("loggingIn", false);
        }
    },

    async logout({ commit }) {
        clearSsoLinkRequest();

        // SSO sessions also end the user's Authentik session, otherwise the next "Sign in with SSO"
        // would silently sign them straight back in.
        // AuthMethod is only "SSO" when the user signed in with it, so this doesn't depend on
        // the SSO options having loaded.
        if (state.user?.authMethod === "SSO") {
            commit("clear");
            logoutOfSso();
            return;
        }

        await logout();
        commit("clear");

        if(!currentRouteIsPublic()) {
            goTo("/login");
        }
    },
    // The router guard relies on these (e.g. to send users to SSO onboarding when it's
    // required), so a failure is always reported, and the guard tries again on the next
    // navigation rather than carrying on as if SSO were off. They can't change while the app
    // is open, so once loaded they aren't fetched again.
    async loadSsoOptions({ commit, dispatch, state }) {
        if (state.ssoOptionsLoaded) {
            return;
        }

        for (let attempt = 1; attempt <= 2; attempt++) {
            try {
                commit("ssoOptions", await getSsoOptions());
                return;
            }
            catch {
                if (attempt === 1) {
                    await new Promise(resolve => setTimeout(resolve, 500));
                }
            }
        }

        const message = "Sign in settings couldn't be loaded. "
            + "Please check your connection and refresh the page.";
        dispatch("alert/error", message, { root: true });
    },
    async enterApplication({ dispatch }) {
        await dispatch("refreshUser");
        if (!state.user.isFullyAuthenticated) {
            goTo("/two-factor-authentication");
        }
        else {
            goTo("/");
        }
    },
    async refreshUser({ commit }) {
        const user = await currentUser();
        if(user !== null) {
            commit("user", user);
        }
    },
    setSettings({ commit, dispatch }, settings) {
        commit("settings", settings);
        dispatch("localisation/load", null, { root: true });
    },
};

const mutations = {
    user(state, user) {
        state.user = user;
        state.policies = new Set(user.policies);
        state.isLoggedIn = true;
    },
    settings(state, settings) {
        if(state.user) {
            Object.assign(state.user, settings);
        }
    },
    ssoOptions(state, ssoOptions) {
        state.ssoOptions = ssoOptions;
        state.ssoOptionsLoaded = true;
    },
    loaded(state) {
        state.isLoaded = true;
    },
    loggingIn(state, loggingIn) {
        state.loggingIn = loggingIn;
    },
    clear(state) {
        state.user = null;
        state.isLoggedIn = false;
        state.loggingIn = false;
    }
};

export const authentication = {
    namespaced: true,
    state,
    getters,
    actions,
    mutations
}
